import React, { useContext, useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import Fade from '@mui/material/Fade';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import { DeleteConfirmationModalContext } from '../../../sharedModules/contexts/DeleteConfirmationModalContext';

type Media = {
  id?: number | string;
  url: string;
  content_type?: string | null;
  download_url?: string | null; // If we decide to generate an attachment URL server-side
};

interface ImagePreviewProps {
  image: Media;
  altText: string;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ image, altText }) => {
  const { setModelLabel, setDeleteConfirmationModalOpen, setRecordToDelete, setDeleteURL } =
    useContext(DeleteConfirmationModalContext);

  const [isHovered, setIsHovered] = useState(false);

  if (!image) return null;

  const isVideo = (image.content_type?.startsWith('video/') ?? false) || /\.(mp4|webm|ogg)$/i.test(image.url);

  const handleDeleteClicked = () => {
    setDeleteURL('backflows/backflow_log_images/');
    setModelLabel(isVideo ? 'Video' : 'Image');
    setDeleteConfirmationModalOpen(true);
    setRecordToDelete(image);
  };

  const downloadHref = image.download_url || image.url;

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: 'relative',
        height: '100%',
        transition: 'all 0.3s ease',
        boxShadow: isHovered ? 4 : 1,
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      {isVideo ? (
        <ButtonBase
          component="a"
          href={downloadHref}
          download
          aria-label={`${altText || 'Video'} (download)`}
          sx={{
            display: 'block',
            width: '100%',
            height: 200,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'grey.900',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              transition: 'background-color 0.3s',
              '&:hover': {
                bgcolor: 'grey.800',
              },
            }}
          >
            <PlayArrowIcon
              sx={{
                fontSize: 72,
                color: 'white',
                opacity: 0.9,
                transition: 'transform 0.3s',
                transform: isHovered ? 'scale(1.1)' : 'scale(1)',
              }}
            />
          </Box>
        </ButtonBase>
      ) : (
        <CardMedia
          component="img"
          image={image.url}
          alt={altText}
          sx={{
            height: 200,
            objectFit: 'cover',
            width: '100%',
          }}
        />
      )}

      <Fade in={isHovered}>
        <IconButton
          onClick={handleDeleteClicked}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(211, 47, 47, 0.9)',
            color: 'white',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              bgcolor: 'error.dark',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s',
            boxShadow: 2,
          }}
          size="small"
          aria-label={`Delete ${isVideo ? 'video' : 'image'}`}
          title={`Delete ${isVideo ? 'video' : 'image'}`}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Fade>
    </Card>
  );
};

export default ImagePreview;
